// redux/slices/notificationsSlice.js

import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data ||
  error?.message ||
  fallback;

const clearTokenOnUnauthorized = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch {}
  }
};

const syncFlatNotifications = (state) => {
  state.notifications = Object.values(state.byUid || {}).flat();
};

const normalizeNotifications = (payload, uid) => {
  if (!Array.isArray(payload)) return [];

  return payload.map((notification) => ({
    ...notification,
    _fetchedForUid:
      notification?._fetchedForUid ??
      notification?.uid ??
      uid,
  }));
};

const upsertNotificationInArray = (array, notification) => {
  if (!notification?.id) return array;

  const id = String(notification.id);
  const index = array.findIndex(
    (item) => String(item?.id) === id
  );

  if (index === -1) {
    array.unshift(notification);
  } else {
    array[index] = {
      ...array[index],
      ...notification,
    };
  }

  return array;
};

const removeNotificationFromArray = (array, id) =>
  (array || []).filter(
    (item) => String(item?.id) !== String(id)
  );

/* ========================= THUNKS ========================= */

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      const response = await apiRequest(
        "GET",
        `/notifications/${encodeURIComponent(uid)}`
      );

      const data = response?.data ?? response;

      return Array.isArray(data) ? data : [];
    } catch (error) {
      const status = error?.response?.status;

      // Нет уведомлений или нет доступа — возвращаем пустой список.
      if (status === 403 || status === 404) {
        return [];
      }

      await clearTokenOnUnauthorized(error);

      return rejectWithValue(
        getError(error, "fetchNotifications failed")
      );
    }
  }
);

export const addNotification = createAsyncThunk(
  "notifications/addNotification",
  async (
    {
      uid,
      notification,
    },
    {
      rejectWithValue,
    }
  ) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      if (!notification || typeof notification !== "object") {
        throw new Error("Уведомление не передано");
      }

      const response = await apiRequest(
        "POST",
        `/notifications/${encodeURIComponent(uid)}`,
        notification
      );

      const created =
        response?.data?.notification ||
        response?.data ||
        response;

      return {
        uid,
        notification: created,
      };
    } catch (error) {
      await clearTokenOnUnauthorized(error);

      return rejectWithValue(
        getError(error, "addNotification failed")
      );
    }
  }
);

export const updateNotification = createAsyncThunk(
  "notifications/updateNotification",
  async (
    {
      uid,
      notificationId,
      updates,
    },
    {
      rejectWithValue,
    }
  ) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      if (notificationId == null) {
        throw new Error("ID уведомления не передан");
      }

      if (!updates || typeof updates !== "object") {
        throw new Error("Изменения уведомления не переданы");
      }

      const response = await apiRequest(
        "PATCH",
        `/notifications/${encodeURIComponent(
          uid
        )}/${encodeURIComponent(notificationId)}`,
        updates
      );

      const updated =
        response?.data?.notification ||
        response?.data ||
        response;

      return {
        uid,
        notificationId,
        updates,
        notification: updated,
      };
    } catch (error) {
      await clearTokenOnUnauthorized(error);

      return rejectWithValue(
        getError(error, "updateNotification failed")
      );
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async (
    {
      uid,
      notificationId,
    },
    {
      rejectWithValue,
    }
  ) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      if (notificationId == null) {
        throw new Error("ID уведомления не передан");
      }

      await apiRequest(
        "DELETE",
        `/notifications/${encodeURIComponent(
          uid
        )}/${encodeURIComponent(notificationId)}`
      );

      return {
        uid,
        notificationId,
      };
    } catch (error) {
      await clearTokenOnUnauthorized(error);

      return rejectWithValue(
        getError(error, "deleteNotification failed")
      );
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (
    {
      uid,
      notificationId,
    },
    {
      dispatch,
    }
  ) => {
    return dispatch(
      updateNotification({
        uid,
        notificationId,
        updates: {
          is_read: true,
        },
      })
    ).unwrap();
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (
    {
      uid,
      notifications,
    },
    {
      dispatch,
    }
  ) => {
    if (!uid) {
      throw new Error("UID не передан");
    }

    const unread = (notifications || []).filter(
      (notification) => !notification?.is_read
    );

    const results = await Promise.allSettled(
      unread.map((notification) =>
        dispatch(
          updateNotification({
            uid,
            notificationId: notification.id,
            updates: {
              is_read: true,
            },
          })
        ).unwrap()
      )
    );

    return results;
  }
);

/* ========================= SELECTORS ========================= */

export const selectNotificationsForUid = (state, uid) =>
  state.notifications?.byUid?.[String(uid)] || [];

export const selectAllNotifications = (state) =>
  state.notifications?.notifications || [];

export const selectNotificationsStatus = (state) =>
  state.notifications?.status || "idle";

export const selectNotificationsError = (state) =>
  state.notifications?.error || null;

export const selectUnreadNotificationsForUid = createSelector(
  [
    (state) => state.notifications?.byUid,
    (state, uid) => uid,
  ],
  (byUid, uid) => {
    if (!uid) return [];

    return (byUid?.[String(uid)] || []).filter(
      (notification) => !notification?.is_read
    );
  }
);

export const selectUnreadNotificationsCount = createSelector(
  [(state) => state.notifications?.notifications],
  (notifications = []) =>
    notifications.filter(
      (notification) => !notification?.is_read
    ).length
);

export const selectNotificationsSorted = createSelector(
  [(state) => state.notifications?.notifications],
  (notifications = []) =>
    [...notifications].sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime()
    )
);

/* ========================= STATE ========================= */

const initialState = {
  notifications: [],
  byUid: {},
  selectedNotification: null,

  status: "idle",
  addStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",

  error: null,
};

/* ========================= SLICE ========================= */

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,

  reducers: {
    patchNotificationLocal: (state, action) => {
      const {
        id,
        changes,
      } = action.payload || {};

      if (id == null) return;

      const patch = changes || {};

      const flatIndex = state.notifications.findIndex(
        (item) => String(item?.id) === String(id)
      );

      if (flatIndex !== -1) {
        state.notifications[flatIndex] = {
          ...state.notifications[flatIndex],
          ...patch,
        };
      }

      Object.keys(state.byUid || {}).forEach((uid) => {
        const array = state.byUid[uid] || [];

        const index = array.findIndex(
          (item) => String(item?.id) === String(id)
        );

        if (index !== -1) {
          array[index] = {
            ...array[index],
            ...patch,
          };
        }

        state.byUid[uid] = array;
      });

      if (
        String(state.selectedNotification?.id) ===
        String(id)
      ) {
        state.selectedNotification = {
          ...state.selectedNotification,
          ...patch,
        };
      }
    },

    removeNotificationLocal: (state, action) => {
      const id = action.payload;

      state.notifications = removeNotificationFromArray(
        state.notifications,
        id
      );

      Object.keys(state.byUid || {}).forEach((uid) => {
        state.byUid[uid] = removeNotificationFromArray(
          state.byUid[uid],
          id
        );
      });

      if (
        String(state.selectedNotification?.id) ===
        String(id)
      ) {
        state.selectedNotification = null;
      }
    },

    setSelectedNotification: (state, action) => {
      state.selectedNotification = action.payload || null;
    },

    clearSelectedNotification: (state) => {
      state.selectedNotification = null;
    },

    clearNotificationsError: (state) => {
      state.error = null;
    },

    resetNotificationsStatus: (state) => {
      state.status = "idle";
      state.addStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },

    clearNotifications: (state) => {
      state.notifications = [];
      state.byUid = {};
      state.selectedNotification = null;
      state.status = "idle";
      state.addStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchNotifications.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = "succeeded";

        const uid = String(action.meta.arg);
        const notifications = normalizeNotifications(
          action.payload,
          uid
        );

        state.byUid[uid] = notifications;
        syncFlatNotifications(state);
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchNotifications failed";
      })

      // ADD
      .addCase(addNotification.pending, (state) => {
        state.addStatus = "loading";
        state.error = null;
      })
      .addCase(addNotification.fulfilled, (state, action) => {
        state.addStatus = "succeeded";

        const {
          uid,
          notification,
        } = action.payload || {};

        if (!uid || !notification) return;

        const key = String(uid);

        const normalized = {
          ...notification,
          _fetchedForUid:
            notification?._fetchedForUid ??
            notification?.uid ??
            key,
        };

        if (!state.byUid[key]) {
          state.byUid[key] = [];
        }

        upsertNotificationInArray(
          state.byUid[key],
          normalized
        );

        syncFlatNotifications(state);
      })
      .addCase(addNotification.rejected, (state, action) => {
        state.addStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "addNotification failed";
      })

      // UPDATE
      .addCase(updateNotification.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";

        const {
          uid,
          notificationId,
          updates,
          notification,
        } = action.payload || {};

        if (notificationId == null) return;

        const updated = {
          ...(updates || {}),
          ...(notification || {}),
          id:
            notification?.id ??
            notificationId,
        };

        const id = String(notificationId);

        const flatIndex = state.notifications.findIndex(
          (item) => String(item?.id) === id
        );

        if (flatIndex !== -1) {
          state.notifications[flatIndex] = {
            ...state.notifications[flatIndex],
            ...updated,
          };
        }

        if (uid != null) {
          const key = String(uid);
          const array = state.byUid[key] || [];

          const index = array.findIndex(
            (item) => String(item?.id) === id
          );

          if (index !== -1) {
            array[index] = {
              ...array[index],
              ...updated,
            };
          }

          state.byUid[key] = array;
        } else {
          Object.keys(state.byUid || {}).forEach((key) => {
            const array = state.byUid[key] || [];

            const index = array.findIndex(
              (item) => String(item?.id) === id
            );

            if (index !== -1) {
              array[index] = {
                ...array[index],
                ...updated,
              };
            }

            state.byUid[key] = array;
          });
        }

        if (
          String(state.selectedNotification?.id) === id
        ) {
          state.selectedNotification = {
            ...state.selectedNotification,
            ...updated,
          };
        }

        syncFlatNotifications(state);
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "updateNotification failed";
      })

      // DELETE
      .addCase(deleteNotification.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";

        const {
          uid,
          notificationId,
        } = action.payload || {};

        state.notifications = removeNotificationFromArray(
          state.notifications,
          notificationId
        );

        if (uid != null) {
          const key = String(uid);

          state.byUid[key] = removeNotificationFromArray(
            state.byUid[key],
            notificationId
          );
        } else {
          Object.keys(state.byUid || {}).forEach((key) => {
            state.byUid[key] = removeNotificationFromArray(
              state.byUid[key],
              notificationId
            );
          });
        }

        if (
          String(state.selectedNotification?.id) ===
          String(notificationId)
        ) {
          state.selectedNotification = null;
        }

        syncFlatNotifications(state);
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "deleteNotification failed";
      })

      // RESET
      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const {
  patchNotificationLocal,
  removeNotificationLocal,
  setSelectedNotification,
  clearSelectedNotification,
  clearNotificationsError,
  resetNotificationsStatus,
  clearNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
